import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import { LatLngExpression, LatLngBoundsExpression, Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ChoroplethLayer } from './Layers/ChoroplethLayer';
import { PointLayers } from './Layers/PointLayers';
import MapRadioControl from '../inputs/MapRadioControl';
import MapZoomControl from '../inputs/MapZoomControl';
import MapClearPointsControl from '../inputs/MapClearPointsControl';
import MapHighlightedFeature from '../inputs/MapHighlightedFeature';
import L from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import CreatePanes from './Layers/CreatePanes';
import { getOrderedQueryString, useGlobalActivePage } from '../../data/StatusStore';
import { siteConfig } from '../../config/siteConfig';

// VectorTileLayer component to handle vector tile layers on the map
const VectorTileLayer = ({ url, pane, id }: { url: string; pane: string, id: string }) => {
  const map = useMap();
  //console.log("B022725 map", map);
  useEffect(() => {
    if (!map || !url || !pane) return;

    let layer: any;

    const addLayer = async () => {
      //console.log("B022725 layer", layer);
      try {
        // Add the vector tile layer
        layer = (L as any).maplibreGL({
          style: url,
          pane: pane,
          id: id,
        }).addTo(map);
      } catch (error) {
        console.error("Error adding vector tile layer:", error);
      }
    };

    addLayer();

    return () => {
      if (layer) {
        try {
          map.removeLayer(layer);
        } catch (error) {
          console.error("Error removing vector tile layer:", error);
        }
      }
    };
  }, [url, pane, map, id]);

  return null;
};

// Component to reset the map's zoom level
const MapWithZoomResetControl: React.FC<{ initialPosition: LatLngExpression, initialZoom:number }> = ({ initialPosition, initialZoom }) => {
  const map = useMap();
  const handleZoomReset = () => {
    console.log("E041525 initialPosition", initialPosition);
    map.setView(initialPosition, initialZoom); // Reset to the initial center and zoom level
  };

  return <MapZoomControl onClickButton={handleZoomReset} />;
};

// Component to reset the map's zoom level
const MapWithClearPointsControl: React.FC<{}> = () => {
  const activePage = useGlobalActivePage();
  return <MapClearPointsControl onlyLogo={ activePage !== 'explorer' ? true : false  }/>;
};

interface DataItem {
  id: string;
  value: any;
}

export interface MainMapProps {
  dataArray: DataItem[];
  keyIndexDataArray: any;
  dataJSON: any;
  dataMeta: any;
  allData: any;
  chartId: string;
  height?: string;
  width?: string;
  mapType: string;
  activeGeography: string;
  unGroupedModules: any;
  setModuleIdIncrement?: Function | undefined;
}

// A small component to initialize the custom pane after the map is ready
const MapInitializer: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    map.createPane('pointsPane');
    const pane = map.getPane('pointsPane');
    if (pane) {
      pane.style.zIndex = '651';
    }
    map.createPane('polyPane');
    const polypane = map.getPane('polyPane');
    if (polypane) {
      polypane.style.zIndex = '650';
    }
  }, [map]);

  return null;
};

// SBC-only: replaces native scroll zoom with exact 0.5-step increments
const SBCScrollZoom: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (debounceTimer) return; // ignore additional ticks in same gesture
      debounceTimer = setTimeout(() => { debounceTimer = null; }, 200);
      const delta = e.deltaY < 0 ? 0.5 : -0.5;
      map.setZoom(map.getZoom() + delta, { animate: true });
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [map]);

  return null;
};

export const MainMap: React.FC<MainMapProps> = ({
  keyIndexDataArray, dataArray, dataJSON, dataMeta, allData, chartId, height, width, mapType, activeGeography, unGroupedModules, setModuleIdIncrement
}) => {
  
  const activePage = useGlobalActivePage();
  const location = useLocation();
  const navigate = useNavigate();
  //console.log("A120624 dataMeta", dataMeta);
  // Extract URL parameters for lat, lng, and zoom
  const searchParams = new URLSearchParams(location.search);
  const lat = parseFloat(searchParams.get('lat') || String(siteConfig.map.center[0]));
  const lng = parseFloat(searchParams.get('lng') || String(siteConfig.map.center[1]));
  const zoom = parseFloat(searchParams.get('zoom') || String(siteConfig.map.zoom));
  const _lat = siteConfig.map.center[0];
  const _lng = siteConfig.map.center[1];
  const _zoom = siteConfig.map.zoom;

  const initialPosition: LatLngExpression = [_lat, _lng]; // Set initial position
  const initialZoom = _zoom; // Set initial position
  const position: LatLngExpression = [lat, lng];
  const bounds: LatLngBoundsExpression = siteConfig.map.bounds;

  const [key, setKey] = useState(0);

  useEffect(() => {
    //console.log("A012425 keyIndexDataArray", keyIndexDataArray);
    //console.log("A012425 dataArray", dataArray);
    //console.log("A012425 dataJSON", dataJSON);
    //console.log("A012425 dataMeta", dataMeta);
    setKey(prevKey => prevKey + 1);
  }, [keyIndexDataArray, dataArray, dataJSON, dataMeta, activeGeography]);

  const mapRadioOptions = [
    {
      category: "by Geography",
      options: [
        { label: "Borough", value: "Boro", 
          enabled: true, 
          available: (activePage === "explorer" || activePage === "stories" || activePage === "rg") ? true : false },
        { label: siteConfig.ctx === 'sbc' 
            ? `City` 
            : `${siteConfig.geographies['CD']?.label ?? 'Community District'} (${siteConfig.geographies['CD']?.shortLabel ?? 'CD'})`, value: "CD", 
          enabled: true, 
          available: true },
        { label: "Neighborhood (NTA)", value: "NTA", 
          enabled: true, 
          available: activePage === "rg" ? false : true },
        { label: "Tract", value: "Tract", 
          enabled: true, 
          available: (activePage === "dashboard" && siteConfig.ctx !== 'sbc') || activePage === "compare" || activePage === "rg" ? false : true},
      ].filter(opt => siteConfig.geographies[opt.value]?.enabled !== false),
    },
    /*{
      category: "by Race",
      options: [
        { label: "all", value: "all", enabled: false },
        { label: "black", value: "black", enabled: false },
        { label: "white", value: "white", enabled: false },
        { label: "asian", value: "asian", enabled: false },
        { label: "latino", value: "latino", enabled: false },
        { label: "other", value: "other", enabled: false },
      ],
    },
    {
      category: "by Gender",
      options: [
        { label: "all", value: "all", enabled: false },
        { label: "female", value: "female", enabled: false },
        { label: "male", value: "male", enabled: false },
      ],
    },*/
  ];

  const handleRadioChange = (value: string) => {
    console.log("Selected Radio Value:", value);
    // Implement your handling logic
  };

  const updateUrlParams = (newLat: number, newLng: number, newZoom: number) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('lat', newLat.toFixed(5));
    newSearchParams.set('lng', newLng.toFixed(5));
    newSearchParams.set('zoom', newZoom.toString());
    let updatedOrderedQueryString = getOrderedQueryString(newSearchParams);
    navigate(`?${updatedOrderedQueryString.toString()}`, { replace: true });
  };

  const MapWithHandlers: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      const debounce = (func: Function, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: any) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func(...args), wait);
        };
      };

      const handleMoveEnd = debounce(() => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        updateUrlParams(center.lat, center.lng, zoom);
      }, 300); // Adjust the debounce delay as needed

      map.on('moveend', handleMoveEnd);

      return () => {
        map.off('moveend', handleMoveEnd);
      };
    }, [map]);

    return null;
  };

  return (
    <>
      <MapContainer
        center={position}
        zoom={zoom}
        zoomDelta={siteConfig.ctx === 'sbc' ? 0.5 : 1}
        zoomSnap={siteConfig.ctx === 'sbc' ? 0.25 : 1}
        zoomAnimation={true}
        scrollWheelZoom={siteConfig.ctx === 'sbc' ? false : true}
        zoomControl={false}
        style={{
          height: height ? height : "100px",
          width: width ? width : "100%",
        }}
        id={"mainMapContainer"}
        maxBounds={bounds}
        maxBoundsViscosity={0}
        minZoom={siteConfig.map.minZoom} // Set your minimum zoom level
        maxZoom={25} // Set your maximum zoom level
        attributionControl={false} // Disable the attribution control
        key={`main_${key}_map_${chartId}`}
      >
        <CreatePanes />
        <MapWithHandlers />
        <MapInitializer />
        {siteConfig.ctx === 'sbc' && <SBCScrollZoom />}

        {/* Background vector tile layer */}
        <VectorTileLayer
          //url="/data/geo/alidade_smooth_base_031925.json" // https://maplibre.org/maputnik
          url={siteConfig.ctx === 'sbc'
            ? "/data/geo/alidade_smooth_base_031925_MOA_v1.2.json"
            : "/data/geo/alidade_smooth_base_031925_MOA_v1.1.json"} // https://maplibre.org/maputnik
          pane="vectorTilePane"
          id={"vectorTileBaseLayer"}
        />

        {/* Pont FOI layers */}
        {(activePage === "explorer" || activePage === "stories") ? (
          <PointLayers
            activeGeography={activeGeography}
            pane="pointsPane"
            polypane="polyPane"
            setModuleIdIncrement={setModuleIdIncrement}
          />
        ) : null}
        {/* Choropleth layer */}
        {dataArray ? (
          <ChoroplethLayer
            _keyIndexDataArray={keyIndexDataArray}
            _dataArray={dataArray}
            dataJSON={dataJSON}
            activeGeography={activeGeography}
            dataMeta={dataMeta}
            chartId={chartId}
            mapType={mapType}
            unGroupedModules={unGroupedModules}
          />
        ) : null}

        {/* Labels vector tile layer */}
        <VectorTileLayer
          //url="/data/geo/alidade_smooth_labels_031925.json" // https://maplibre.org/maputnik
          url={siteConfig.ctx === 'sbc'
            ? "/data/geo/alidade_smooth_labels_031925_v1.2.json"  // grey #555 labels for SBC white basemap
            : "/data/geo/alidade_smooth_labels_031925_wht.json"} // https://maplibre.org/maputnik
          pane="vectorTileLabelPane"
          id="vectorTileLabelLayer"
        />

        {/* Map radio control */}
        { activePage !== "stories"
        ? <MapRadioControl
            categoryOptions={mapRadioOptions}
            defaultValue="1"
            onChange={handleRadioChange}
            dataMeta={dataMeta}
            allData={allData}
            activePage={activePage}
          />
        : null
        }

        {/* Map zoom reset control AND/OR MOA logo*/}
        <MapWithClearPointsControl/>
        <MapWithZoomResetControl initialPosition={initialPosition} initialZoom={initialZoom} />
        <MapHighlightedFeature dataJSON={dataJSON} />
      </MapContainer>
    </>
  );
};

export default MainMap;
